import React, { useState, useEffect } from 'react';
import DiversificationOpportunityMatrix from './DiversificationOpportunityMatrix';
import AllianceVsRivalAnalysis from './AllianceVsRivalAnalysis';
import StrategicIntelligenceAlerts from './StrategicIntelligenceAlerts';
import { diversificationMatrixService } from '../services/diversificationMatrixApi';
import { useTheme } from '../context/ThemeContext';

const DiversificationStrategicAnalysis = ({ selectedMineral: parentSelectedMineral = 'Lithium' }) => {
  const { isDarkMode } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMineral, setSelectedMineral] = useState(parentSelectedMineral);

  // Sync with parent component mineral selection
  useEffect(() => {
    setSelectedMineral(parentSelectedMineral);
  }, [parentSelectedMineral]);

  // Load all data when mineral changes
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await diversificationMatrixService.getMatrixData(selectedMineral);
        setData(result);
      } catch (err) {
        console.error('Error loading diversification strategic analysis:', err);
        setError(err.message);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedMineral]);

  // Error state when no data is available
  if (error || !data) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 transition-colors duration-200">
          <div className="flex items-center">
            <div className="text-red-800 dark:text-red-200 text-sm">
              <strong>Error:</strong> Unable to load diversification analysis data. 
              {error && ` ${error}`}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state for the entire section
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 transition-colors duration-200">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-4"></div>
              <div className="h-64 bg-gray-200 dark:bg-gray-600 rounded"></div>
            </div>
          </div>
          <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 transition-colors duration-200">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-4"></div>
              <div className="h-64 bg-gray-200 dark:bg-gray-600 rounded"></div>
            </div>
          </div>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6 transition-colors duration-200">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-1/2 mb-4"></div>
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-20 bg-gray-300 dark:bg-gray-600 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mineral Selection */}
      <div className="flex items-center justify-start mb-4">
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Mineral:</label>
          <select
            value={selectedMineral}
            onChange={(e) => {
              setSelectedMineral(e.target.value);
            }}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 px-3 py-2 transition-colors duration-200"
          >
            <option value="Lithium">Lithium</option>
            <option value="Cobalt">Cobalt</option>
            <option value="Rare Earth Elements">Rare Earth Elements</option>
            <option value="Graphite">Graphite</option>
            <option value="Nickel">Nickel</option>
          </select>
        </div>
      </div>

      {/* Main Analysis Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Diversification Opportunity Matrix - Takes 2/3 of the width */}
        <div className="lg:col-span-2">
          <DiversificationOpportunityMatrix 
            selectedMineral={selectedMineral}
          />
        </div>

        {/* Right: Strategic Intelligence Alerts - Takes 1/3 of the width */}
        <div className="lg:col-span-1">
          <StrategicIntelligenceAlerts 
            strategicAlerts={data?.strategicAlerts}
            selectedMineral={selectedMineral}
            geopoliticalAnalysis={data?.geopoliticalAnalysis}
          />
        </div>
      </div>

      {/* Commented out: Alliance vs Rival Analysis */}
      {/* 
      <div className="mt-6">
        <AllianceVsRivalAnalysis 
          geopoliticalData={data?.geopoliticalAnalysis}
          selectedMineral={selectedMineral}
        />
      </div>
      */}
    </div>
  );
};

export default DiversificationStrategicAnalysis;
